/**
 * **`useLeaveGuard(dirty)`** — leaving with unsaved changes asks first (W28,
 * FR-65, plan D84), copy-adapted from ProDesk's
 * (`repo:prodesk:apps/web/src/components/edit/useLeaveGuard.tsx`):
 *
 * - **A route change** is held by React Router's blocker and asked about in a
 *   `ConfirmDialog` — *Leave* or *Stay and save*.
 * - **A tab close or reload** gets the browser's own prompt, the only one a
 *   browser allows there.
 *
 * `dirty` is the page's answer to *"is there anything on this screen that Save
 * would send?"*. The hook returns the dialog; the page renders it anywhere.
 *
 * **Outside a data router the route half is skipped, not thrown** — `useBlocker`
 * throws there, so the choice is made by rendering one of two components
 * rather than by calling a hook conditionally. The app runs under
 * `createBrowserRouter`; a test in a plain `MemoryRouter` loses only the
 * in-app half.
 */
import { ConfirmDialog } from '@fiveox/ui'
import { useContext, useEffect, type ReactNode } from 'react'
import { UNSAFE_DataRouterContext, useBlocker } from 'react-router'

export function useLeaveGuard(dirty: boolean): ReactNode {
  useEffect(() => {
    if (!dirty) return
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault()
      // The legacy spelling some browsers still want before they will ask.
      event.returnValue = ''
    }
    window.addEventListener('beforeunload', warn)
    return () => window.removeEventListener('beforeunload', warn)
  }, [dirty])

  return <LeaveGuard dirty={dirty} />
}

function LeaveGuard({ dirty }: { dirty: boolean }) {
  const dataRouter = useContext(UNSAFE_DataRouterContext)
  return dataRouter ? <BlockingGuard dirty={dirty} /> : null
}

function BlockingGuard({ dirty }: { dirty: boolean }) {
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) => dirty && (currentLocation.pathname !== nextLocation.pathname || currentLocation.search !== nextLocation.search),
  )
  return (
    <ConfirmDialog
      open={blocker.state === 'blocked'}
      onClose={() => blocker.reset?.()}
      title="Leave without saving?"
      confirmLabel="Leave"
      cancelLabel="Stay and save"
      tone="danger"
      onConfirm={() => blocker.proceed?.()}
    >
      <p className="text-muted-foreground text-sm">You’ve changed something on this page and haven’t saved it. If you leave now, the change is lost.</p>
    </ConfirmDialog>
  )
}
